<template>
  <div class="panel-content">
    <div v-if="rsGroups.length > 0" class="mb-4">
      <div v-for="group in rsGroups" :key="group.id">
        <div class="pane-top">
          <div class="pane-top__inner">
            <UserCardIcon @mousedown.stop />
            <span class="pane-title">{{ group.name }}</span>
            <button
              class="delete-all-btn"
              v-if="!isVisitLocked"
              @click="deleteAllUserAdded(group)"
            >
              <DeselectAllIcon @mousedown.stop />
              Delete All
            </button>
          </div>
        </div>
        <div class="brands-list">
          <div class="brand-section">
            <div class="supplement-items">
              <div
                v-for="supplement in group.supplements?.$values"
                :key="supplement.id"
                class="supplement-item rs-group-item"
                :class="{ 'is-highlighted': highlightedSupplementId === supplement.supplementId }"
                @mouseenter="$emit('supplement-highlight', supplement.supplementId)"
                @mouseleave="$emit('supplement-unhighlight')"
              >
                <div class="supplement-info"
                     :class="{'disabled': visit.status === 'Completed'}"
                >
                  <div style="height: 24px"
                       class="supplement-icon"
                       :class="{'disabled': ['InProtocol', 'Completed', 'Closed', 'ProtocolEnding'].includes(visit.status)}"
                  >
                    <CheckCircleGreenIcon
                      @click="deleteUserAddedByValue(supplement, group.name)"
                      @mousedown.stop
                    />
                  </div>
                  <span
                    class="supplement-name"
                    @click="$emit('show-crg-notes', supplement)"
                  >
                    {{ supplement.name }}
                  </span>
                  <supplement-icons
                    :supplement="supplement"
                    @show-crg-notes="$emit('show-crg-notes', supplement)"
                    @warning-notes="$emit('warning-notes', supplement)"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!--    rsGroups-->

    <div class="pane-top">
      <p class="pane-top__inner pane-top__inner--clickable"
         :class="{ 'is-locked': isVisitLocked }"
         @click="openSettingsDialog"
      >
        <span
          class="settings-btn"
          v-if="!isVisitLocked"
          @mousedown.stop
        >
          <GearIcon />
        </span>
        <span class="pane-title">{{ title }}</span>
      </p>
    </div>

    <div class="brands-list">
      <div
        v-for="brand in computedBrandSets"
        :key="brand.code + '-' + brand.id"
        class="brand-section"
        :class="{
          'collapsed': isCollapsed(brand.code),
          'no-selected': brand.selectedCount === 0
        }"
      >
        <div
          class="brand-header"
          @click="toggleBrand(brand.code)"
        >
          <div class="brand-info">
            <ChevronBlackIcon @mousedown.stop />
            <h6 class="brand-name">{{ brand.name }}</h6>
            <span
              v-if="brand.displayCount > 0"
              class="supplement-count"
              :class="{ 'has-selected': brand.hasSelected }"
            >
              {{ brand.displayCount }}
            </span>
            <span v-if="brand.aiAssisted" class="ai-assisted">AI Assisted</span>
          </div>
        </div>
        <div v-if="!isCollapsed(brand.code)" class="brand-content">
          <template v-if="!brand.groups.length">
            <div class="no-recommendations">
              No recommendations
            </div>
          </template>
          <template v-else>
            <div
              v-for="group in brand.groups"
              :key="group.name"
              class="group-section"
            >
              <div class="group-header">
                <div class="group-info">
                  <ResultGroupSurveyIcon v-if="group.name === 'Survey'" @mousedown.stop />
                  <ResultGroupBloodIcon v-else @mousedown.stop />
                  <span class="group-name">{{ group.name }}</span>
                  <template v-if="group.supplements && group.supplements.length">
                    <button
                      class="select-all-btn"
                      v-if="!isVisitLocked"
                      @click="toggleAllInGroup(brand.code, group.name)"
                    >
                      <SelectAllIcon v-if="!isAllSelectedInGroup(group)" @mousedown.stop />
                      <DeselectAllIcon v-else @mousedown.stop />
                      {{ isAllSelectedInGroup(group) ? 'Deselect All' : 'Select All' }}
                    </button>
                    <span class="total-score">total score</span>
                  </template>
                  <template v-else>
                    <div class="no-recommendations">
                      – No recommendations
                    </div>
                  </template>
                </div>
              </div>
              <div class="supplement-items"
                   :class="group.name === 'Survey' ? 'supplement-items--survey' : 'supplement-items--blood'"
              >
                <div v-for="(supplement, index) in group.supplements"
                     :key="`${supplement.id}-${index}`"
                     :class="[
                       'supplement-item',
                       {
                         selected: supplement.selected,
                         'is-highlighted': highlightedSupplementId === supplement.supplementId,
                         'is-hidden': !supplement.selected && isVisitLocked
                       }
                     ]"
                     @click="$emit('supplement-selected', supplement)"
                     @mouseenter="$emit('supplement-highlight', supplement.supplementId)"
                     @mouseleave="$emit('supplement-unhighlight')"
                     draggable="true"
                     @dragstart="$emit('supplement-dragged', supplement)"
                >
                  <div class="supplement-info"
                       :class="{'disabled': visit.status === 'Completed'}"
                  >
                    <div class="supplement-checkbox">
                      <checkbox
                        :value="supplement.selected"
                        :class="{'disabled': ['InProtocol', 'Completed', 'Closed', 'ProtocolEnding'].includes(visit.status)}"
                        @change="(value) => toggleSupplementByValue(supplement, value, brand, group.name)"
                        :custom-icons="true"
                        :icon-component="getSupplementIconComponent(supplement)"
                      />
                    </div>
                    <span
                      class="supplement-name"
                      @click="$emit('show-crg-notes', supplement)"
                    >
                    {{ supplement.name }}
                  </span>
                    <supplement-icons
                      :supplement="supplement"
                      @show-crg-notes="$emit('show-crg-notes', supplement)"
                      @warning-notes="$emit('warning-notes', supplement)"
                    />
                  </div>
                  <span class="supplement-score">{{ supplement.score }}</span>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import draggable from 'vuedraggable'
import checkbox from '@/components/elements/checkbox'
import SupplementIcons from '@/components/elements/supplement-icons'
import GearIcon from '@/assets/icons/components/gear-icon.svg'
import UserCardIcon from '@/assets/icons/components/user-card.svg'
import ChevronBlackIcon from '@/assets/icons/components/chevron-black.svg'
import SelectAllIcon from '@/assets/icons/components/select-all-icon.svg'
import DeselectAllIcon from '@/assets/icons/components/deselect-all-icon.svg'
import ResultGroupSurveyIcon from '@/assets/icons/components/result-group-survey.svg'
import ResultGroupBloodIcon from '@/assets/icons/components/result-group-blood.svg'
import CheckCircleGreenIcon from '@/assets/icons/components/check-circle-green.svg'
import PlusCircleGreyIcon from '@/assets/icons/components/plus-circle-grey.svg'

export default {
  name: 'SupplementsLeftPanel',
  components: {
    checkbox,
    draggable,
    GearIcon,
    UserCardIcon,
    ChevronBlackIcon,
    SelectAllIcon,
    DeselectAllIcon,
    ResultGroupSurveyIcon,
    ResultGroupBloodIcon,
    CheckCircleGreenIcon,
    PlusCircleGreyIcon,
    SupplementIcons
  },
  props: {
    title: {
      type: String,
      default: 'Supplement Recommender'
    },
    brandSets: {
      type: Array,
      default: () => []
    },
    rsGroups: {
      type: Array,
      default: () => []
    },
    highlightedSupplementId: {
      type: [Number, String],
      default: null
    }
  },
  data() {
    return {
      // collapsedBrands: ['MH']
      collapsedBrands: [],
      openedBrandCode: null,
      checkIconComponent: CheckCircleGreenIcon,
      uncheckIconComponent: PlusCircleGreyIcon
    }
  },
  computed: {
    ...mapState({
      visit: state => state.visits?.visit,
    }),

    isVisitLocked() {
      return ['InProtocol', 'Completed', 'Closed', 'ProtocolEnding']
        .includes(this.visit?.status)
    },

    computedBrandSets() {
      return this.brandSets.map((brand) => {
        const groups = brand.groups?.map(g => ({
          ...g,
          supplements: g.supplements || []
        })) || []

        const selectedCount = groups.reduce(
          (sum, g) => sum + g.supplements.filter((s) => s.selected).length,
          0
        )

        const totalCount = groups.reduce(
          (sum, g) => sum + g.supplements.length,
          0
        )

        return {
          ...brand,
          groups,
          selectedCount,
          displayCount: selectedCount > 0 ? selectedCount : totalCount,
          hasSelected: selectedCount > 0
        }
      })
    }
  },
  watch: {
    brandSets: {
      handler(newVal) {
        if (!newVal || !newVal.length) return

        if (this.openedBrandCode) return

        const preselectedBrand = this.computedBrandSets.find(b => b.selected)

        if (preselectedBrand) {
          this.openedBrandCode = preselectedBrand.code
        } else {
          this.openedBrandCode = this.computedBrandSets[0].code
        }

        this.collapsedBrands = this.computedBrandSets
          .filter(b => b.code !== this.openedBrandCode)
          .map(b => b.code)
      },
      immediate: true,
      deep: true
    }
  },

  methods: {
    ...mapActions('results', [
      'updateResult',
      'addSupplementToResultBrand',
      'deleteSupplementFromResultBrand',
      'addAllResultBrands',
      'deleteAllResultBrands'
    ]),

    async openSettingsDialog() {
      if (this.isVisitLocked) return
      this.$emit('open-settings')
      // await this.fetchAndShowBrandsDialog()
    },
    isAllSelectedInGroup(group) {
      return group.supplements.length > 0 && group.supplements.every(s => s.selected)
    },
    toggleBrand(brandCode) {
      this.openedBrandCode = this.openedBrandCode === brandCode ? null : brandCode
      this.collapsedBrands = this.computedBrandSets
        .filter(b => b.code !== this.openedBrandCode)
        .map(b => b.code)
    },
    isCollapsed(brandCode) {
      return this.openedBrandCode !== brandCode
    },

    async handleSupplementAction({ supplement, brand, groupName, shouldAdd }) {
      const { id: visitId } = this.$route.params

      const type =
        groupName === 'Survey'
          ? 'SurveyCalculation'
          : groupName === 'Blood'
            ? 'BloodCalculation'
            : 'Manual'

      const payload = {
        id: supplement.id,
        supplementId: supplement.supplementId,
        type,
        ...(brand && { resultBrandSetId: brand.id })
      }
      try {
        const action = shouldAdd
          ? this.addSupplementToResultBrand
          : this.deleteSupplementFromResultBrand

        await action({ visitId, payload })

        this.$emit('supplement-toggled', { supplement })
      } catch (err) {
        console.error('Error handling supplement:', err)
      }
    },

    async deleteUserAddedByValue(supplement) {
      this.$emit('supplement-highlight', supplement.supplementId)

      this.handleSupplementAction({
        supplement,
        shouldAdd: false
      })

      setTimeout(() => {
        this.$emit('supplement-unhighlight')
      }, 1500)
    },

    async toggleSupplementByValue(supplement, value, brand, groupName) {
      this.$emit('supplement-highlight', supplement.supplementId)

      this.handleSupplementAction({
        supplement,
        brand,
        groupName,
        shouldAdd: value
      })

      setTimeout(() => {
        this.$emit('supplement-unhighlight')
      }, 1500)
    },

    async deleteAllUserAdded(group) {
      const { id } = this.$route.params
      const type = 'Manual'

      try {
        await this.deleteAllResultBrands({
          visitId: id,
          id: group.id,
          type
        })

      } catch (err) {
        console.error('Error delete all user added:', err)
      }
    },

    async toggleAllInGroup(brandCode, groupName) {
      const { id } = this.$route.params
      const brand = this.brandSets.find(b => b.code === brandCode)
      if (!brand) return

      const groups = brand.groups || []
      const group = groups.find(g => g.name === groupName)
      if (!group) return

      const supplements = group.supplements || []
      const hasUnselected = supplements.some(s => !s.selected)

      const type = groupName === 'Survey' ? 'SurveyCalculation' : 'BloodCalculation'

      if (hasUnselected) {
        await this.addAllResultBrands({
          visitId: id,
          id: brand.id,
          type
        })
      } else {
        await this.deleteAllResultBrands({
          visitId: id,
          id: brand.id,
          type
        })
      }

      this.$emit('group-selection-changed', { brandCode, groupName, selected: hasUnselected })
    },

    getSupplementIconComponent(supplement) {
      return supplement.selected
        ? this.checkIconComponent
        : this.uncheckIconComponent
    },
  }
}
</script>

<style lang="scss" scoped>
.panel-content {
  height: 100%;

  .pane-top {
    display: flex;
    align-items: center;
    column-gap: 7px;
    margin-bottom: 8px;

    &__inner {
      display: flex;
      align-items: center;
      column-gap: 7px;
      margin: 0;

      &--clickable {
        cursor: pointer;

        &.is-locked {
          cursor: auto;
        }
      }
    }

    .pane-title {
      font-size: 12px;
      font-weight: 500;
      line-height: 32px;
      color: #718096;
      text-transform: uppercase;
      margin-bottom: 0;
    }

    .settings-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      border-radius: 10px;
      background-color: #EDF2F7;

      svg {
        pointer-events: none;
      }
    }
  }

  .delete-all-btn,
  .select-all-btn {
    background: none;
    border: none;
    display: flex;
    align-items: center;
    column-gap: 8px;
    color: #008C89;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
  }

  .select-all-btn {
    padding: 0 8px;
  }
}

.brands-list {
  display: flex;
  flex-direction: column;
  row-gap: 4px;
}

.brand-section {
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0px 0px 1px 0px rgba(12, 26, 75, 0.1);
  padding: 10px;
  overflow: hidden;

  svg {
    transition: transform 0.2s ease;
  }

  &.collapsed {
    background-color: rgba(0, 140, 137, 0.1);
    box-shadow: none;
    padding: 4px;

    .brand-header {
      background-color: transparent;
    }

    svg {
      transform: rotate(-90deg);
    }
  }

  &.no-selected {
    &.collapsed {
      background-color: #EBF2FA;
    }

    .brand-header {
      background-color: #EBF2FA;

      .brand-name {
        color: #1A202C;
      }
    }
  }
}

.brand-header {
  border-radius: 8px;
  background-color: rgba(0, 140, 137, 0.1);
  padding: 8px 15px;
  cursor: pointer;

  .brand-info {
    display: flex;
    align-items: center;
    gap: 6px;

    svg {
      width: 16px;
      height: 16px;
    }
  }

  .brand-name {
    font-weight: 600;
    font-size: 16px;
    color: #008C89;
  }

  .supplement-count {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    font-size: 12px;
    font-weight: 600;
    color: #1A202C;
    background-color: #E4ECF7;
    border-radius: 50%;

    &.has-selected {
      flex-shrink: 0;
      color: #ffffff;
      background-color: #008C89;
    }
  }

  .ai-assisted {
    background: rgba(0, 140, 137, 0.1);
    border-radius: 8px;
    font-size: 13px;
    font-weight: 500;
    line-height: 24px;
    color: #008C89;
    padding: 2px 9px;
    margin-left: auto;
  }
}

.group-header {
  padding: 8px 0;

  .group-info {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    font-size: 12px;
    font-weight: 500;
    line-height: 18px;
    color: #718096;
    text-transform: uppercase;

    .total-score {
      padding-right: 10px;
      margin-left: auto;
    }
  }
}

.no-recommendations {
  color: #718096;
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
}

.supplement-items {
  position: relative;

  &::before {
    content: "";
    display: block;
    width: 3px;
    height: 100%;
    border-radius: 2px;
    position: absolute;
    left: 0;
    top: 0;
    z-index: 1;
  }

  &--survey {
    &::before {
      background-color:rgba(159, 182, 255, 0.5);
    }
  }

  &--blood {
    &::before {
      background-color: rgba(246, 142, 135, 0.5);
    }
  }

  .supplement-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    column-gap: 3px;
    min-height: 48px;
    background-color: #ffffff;
    border-width: 0.5px 1px;
    border-style: solid;
    border-color: #E2E8F0;
    padding: 4px 20px 4px 10px;
    transition: all 0.2s ease;

    &.rs-group-item {
      svg {
        cursor: pointer;
      }

      .supplement-info {
        .supplement-icon.disabled {
          cursor: auto;
          pointer-events: none;
        }
      }
    }

    &.is-hidden {
      .supplement-checkbox {
        visibility: hidden;
        pointer-events: none;
      }
    }

    &:hover {
      background-color: #EBF2FA;
    }

    &.selected {
      background-color: #F7FAFC;

      &:hover,
      &.is-highlighted {
        background-color: #EBF2FA;
      }
    }

    .supplement-info {
      display: flex;
      align-items: center;
      column-gap: 10px;
      font-size: 15px;
      font-weight: 600;
      line-height: 20px;
      color: #1A202C;

      .supplement-name {
        cursor: pointer;
      }

      &.disabled {
        cursor: auto;

        &:hover {
          text-decoration: none;
        }

        .supplement-name,
        .icons {
          pointer-events: none;
        }
      }

      &:hover {
        text-decoration: underline;
      }

      .supplement-checkbox {
        .checkbox.disabled {
          pointer-events: none;
          cursor: auto;
        }

        ::v-deep .v-input {
         .v-input--selection-controls__input {
           margin-right: 0;
         }
        }

        svg {
          width: 25px;
          height: 25px;
        }
      }
    }

    .supplement-score {
      font-size: 14px;
      font-weight: 500;
      color: #718096;
    }
  }
}
</style>

<style lang="scss">
.supplement-checkbox {
  svg {
    width: 25px;
    height: 25px;
  }
}
</style>
